/**
 * API utility functions for making requests to the backend
 * Handles proxy routing in development and direct URLs in production
 */

/**
 * Get the API base URL based on environment
 * In development: uses proxy at /api
 * In production: uses direct URL to sperow.in
 */
export const getApiUrl = (endpoint) => {
  // Remove leading slash if present
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  
  // In development, use the Vite proxy
  if (import.meta.env.DEV) {
    return `/api/${cleanEndpoint}`;
  }
  
  // In production, use direct URL
  return `https://pharma-amd-0-1-42.onrender.com/pharma/api/${cleanEndpoint}`;
};

/**
 * Make an authenticated API request
 * @param {string} endpoint - API endpoint (e.g., 'sales/', 'inventory/stock')
 * @param {object} options - Fetch options (method, body, etc.)
 * @returns {Promise<Response>}
 */
export const apiRequest = async (endpoint, options = {}) => {
  const url = getApiUrl(endpoint);
  const authToken = localStorage.getItem('authToken');
  
  const defaultHeaders = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true',
  };
  
  if (authToken) {
    defaultHeaders['Authorization'] = `Bearer ${authToken}`;
  }
  
  const config = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };
  
  // Remove mode in development (proxy handles CORS)
  if (!import.meta.env.DEV && options.mode) {
    config.mode = options.mode;
  }
  
  return fetch(url, config);
};

/**
 * Make a GET request
 */
export const apiGet = (endpoint, options = {}) => {
  return apiRequest(endpoint, { ...options, method: 'GET' });
};

/**
 * Make a POST request
 */
export const apiPost = (endpoint, data, options = {}) => {
  return apiRequest(endpoint, {
    ...options,
    method: 'POST',
    body: JSON.stringify(data),
  });
};

/**
 * Make a PUT request
 */
export const apiPut = (endpoint, data, options = {}) => {
  return apiRequest(endpoint, {
    ...options,
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

/**
 * Make a DELETE request
 */
export const apiDelete = (endpoint, options = {}) => {
  return apiRequest(endpoint, { ...options, method: 'DELETE' });
};
